'use client';

import { KeyRoundIcon, Loader2Icon, WandSparklesIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { ScenarioGenerationDialog } from './ScenarioGenerationDialog';

import type { ScenarioCredentialStatus } from '@/app/sdk';

import { appPreferencesStore } from '@/app/global/appPreferencesStore';
import { sdk } from '@/app/sdk';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface ScenarioGenerationDialogGateProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Gates the Foundry generation dialog on Scenario credential availability.
 * When credentials are missing, presents a lightweight setup-required dialog with a
 * deep link into the global Application Settings → API Keys tab instead of mounting
 * the heavy generation UI (which would otherwise fire failing API calls).
 */
export const ScenarioGenerationDialogGate = observer(({ isOpen, onOpenChange }: ScenarioGenerationDialogGateProps) => {
  const [status, setStatus] = useState<ScenarioCredentialStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset so the next open always re-checks.
      setStatus(null);
      setIsChecking(false);
      return;
    }

    // Set isChecking synchronously before the async call so the very first render
    // with isOpen=true shows the spinner rather than the "not configured" message.
    // Capture to a local so TypeScript can narrow it safely inside the async closure.
    const scenarioApi = sdk?.scenario;
    if (!scenarioApi?.getCredentialStatus) {
      setStatus({ configured: false });
      return;
    }

    setIsChecking(true);
    let cancelled = false;

    const check = async () => {
      try {
        const result = await scenarioApi.getCredentialStatus();
        if (!cancelled) setStatus(result);
      } catch {
        if (!cancelled) setStatus({ configured: false });
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    };

    void check();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (status?.configured) {
    return <ScenarioGenerationDialog isOpen={isOpen} onOpenChange={onOpenChange} />;
  }

  const openSettings = () => {
    onOpenChange(false);
    appPreferencesStore.openAppSettings();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <span className="flex items-center gap-2">
              <WandSparklesIcon className="size-4" />
              Foundry - Setup Required
            </span>
          </DialogTitle>
          <DialogDescription>
            Foundry uses the Scenario AI service to generate skyboxes, textures, images, and 3D models.
            Add your Scenario API key and secret to get started.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          {isChecking ? (
            <span className="flex items-center gap-2">
              <Loader2Icon className="size-3.5 animate-spin" />
              Checking credentials…
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <KeyRoundIcon className="size-3.5" />
              No Scenario credentials configured.
            </span>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={openSettings} disabled={isChecking}>
            Configure credentials
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

ScenarioGenerationDialogGate.displayName = 'ScenarioGenerationDialogGate';
